import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import NetworkCheckIcon from "@mui/icons-material/NetworkCheck";
import { Field, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import Div from "@jumbo/shared/Div/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { Label } from "app/pages/presentation/module/form/types";
import {
  CURRENCY_CODES,
  CURRENCY_CODE_POSITIONS,
  DSOK_LANGUAGES,
  FRONTEND_TYPE,
  LINE_OF_BUSINESS,
  VAT_COUNTRY_CODES,
} from "app/utils/constants/settings";
import { getServicePartners } from "app/services/api/servicePartners";
import { pingDla, getDlaLogsMeta, downloadDlaLogs } from "app/services/api/dlaStatus";

/** POS Integration options: code (stored in API) → labelKey (display). Same pattern as Servisný partner. */
const POS_INTEGRATION_OPTIONS = [
  { value: "none", labelKey: "pages.dsokDetails.posIntegrationOptions.none" },
  { value: "ikelp", labelKey: "pages.dsokDetails.posIntegrationOptions.ikelp" },
  {
    value: "dotykacka",
    labelKey: "pages.dsokDetails.posIntegrationOptions.dotykacka",
  },
  {
    value: "oberon",
    labelKey: "pages.dsokDetails.posIntegrationOptions.oberon",
  },
  {
    value: "storyous",
    labelKey: "pages.dsokDetails.posIntegrationOptions.storyous",
  },
  { value: "agnis", labelKey: "pages.dsokDetails.posIntegrationOptions.agnis" },
];

const COMMUNICATION_TYPE_OPTIONS = [{ value: "ReverseSSH" }, { value: "DLA" }];

const BasicSettingsTab = ({ isPartnerAdmin, readOnly = false }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const { enqueueSnackbar } = useSnackbar();
  const disabled = readOnly;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingActive, setPendingActive] = useState(
    Boolean(values?.customer?.active),
  );
  const [servicePartners, setServicePartners] = useState([]);
  const [servicePartnersLoading, setServicePartnersLoading] = useState(true);

  const [pingOpen, setPingOpen] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);

  const [logsMetaLoading, setLogsMetaLoading] = useState(false);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [logDates, setLogDates] = useState([]);
  const [logsDownloading, setLogsDownloading] = useState(false);

  const handlePingDla = async () => {
    const connectionCode = values?.customer?.dlaConnectionCode;
    const beDomain = values?.customer?.beDomain;
    if (!connectionCode) return;
    setPinging(true);
    setPingResult(null);
    setPingOpen(true);
    try {
      const data = await pingDla(connectionCode, beDomain);
      setPingResult({ ok: true, data });
    } catch (err) {
      const reason =
        err?.response?.data?.error ||
        err?.response?.data?.reason ||
        err?.message ||
        "error";
      setPingResult({ ok: false, error: reason });
    } finally {
      setPinging(false);
    }
  };

  const formatUptime = (seconds) => {
    if (typeof seconds !== "number" || isNaN(seconds)) return "-";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h} hod ${m} min`;
  };

  const formatDateTime = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString();
    } catch (_) {
      return iso;
    }
  };

  const formatDateSk = (dateStr) => {
    if (!dateStr) return dateStr;
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    return parts[2] + "." + parts[1] + "." + parts[0];
  };

  const handleOpenLogsDialog = async () => {
    const connectionCode = values?.customer?.dlaConnectionCode;
    const beDomain = values?.customer?.beDomain;
    if (!connectionCode) return;
    setLogsMetaLoading(true);
    setLogsDialogOpen(true);
    setLogDates([]);
    try {
      const data = await getDlaLogsMeta(connectionCode, beDomain);
      setLogDates(Array.isArray(data?.dates) ? data.dates : []);
    } catch (_) {
      setLogDates([]);
      enqueueSnackbar(
        t("dsokCustomerDetail.dla.logsMetaError", { defaultValue: "Nepodarilo sa načítať zoznam logov" }),
        { variant: "error" },
      );
    } finally {
      setLogsMetaLoading(false);
    }
  };

  const handleDownloadLogs = async (date) => {
    const connectionCode = values?.customer?.dlaConnectionCode;
    const beDomain = values?.customer?.beDomain;
    if (!connectionCode || !date) return;
    setLogsDownloading(true);
    try {
      const blob = await downloadDlaLogs(connectionCode, date, beDomain);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dla-${date}.log`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      enqueueSnackbar(
        t("dsokCustomerDetail.dla.downloadError", { defaultValue: "Nepodarilo sa stiahnuť logy" }),
        { variant: "error" },
      );
    } finally {
      setLogsDownloading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setServicePartnersLoading(true);
      try {
        const list = await getServicePartners();
        if (!cancelled) {
          setServicePartners(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        if (!cancelled) {
          setServicePartners([]);
          enqueueSnackbar(
            t("pages.dsokDetails.servicePartnersLoadError", {
              defaultValue: "Failed to load service partners",
            }),
            { variant: "error" },
          );
        }
      } finally {
        if (!cancelled) setServicePartnersLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar, t]);

  const servicePartnerOptions = useMemo(() => {
    const noneOption = {
      value: 0,
      label: t("pages.dsokDetails.servicePartnerNone", {
        defaultValue: "None",
      }),
    };
    const seen = new Set();
    const fromApi = (servicePartners || [])
      .filter((item) => {
        const id = item?.id ?? item?._id;
        if (id == null || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .sort((a, b) => {
        const na = String(a?.company_name ?? "").trim();
        const nb = String(b?.company_name ?? "").trim();
        return na.localeCompare(nb, undefined, { sensitivity: "base" });
      })
      .map((item) => ({
        value: item?.id ?? item?._id,
        label:
          String(item?.company_name ?? "").trim() ||
          String(item?.id ?? item?._id),
      }));
    return [noneOption, ...fromApi];
  }, [servicePartners, t]);

  const isVatPayer = values?.customer?.vatPayer;
  const frontendType = values?.customer?.frontendType;

  return (
    <>
      {/* isActive toggle */}
      {(!isPartnerAdmin || readOnly) && (
        <>
          <Grid item xs={12} md={4} lg={2}>
            <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(values?.customer?.active)}
                    disabled={disabled}
                    onChange={(e) => {
                      if (readOnly) return;
                      const next = e.target.checked;
                      setPendingActive(next);
                      setConfirmOpen(true);
                    }}
                  />
                }
                label={t("pages.dsokDetails.status") || "Active"}
              />
            </Div>
          </Grid>
          <Grid item xs={12} md={12} lg={12}></Grid>
          <Grid item xs={12} md={4} lg={4}>
            <Div sx={{ mt: 1, mb: 1 }}>
              <JumboTextField
                fullWidth
                name="customer.dlaConnectionCode"
                label="DLA ConnectionCode"
                helperText={t(
                  "dsokCustomerDetail.dlaConnectionCodeHelp",
                  {
                    defaultValue:
                      "Generated automatically and used for DSOK / Datavision Local Agent connection.",
                  },
                )}
                InputProps={{ readOnly: true }}
              />
            </Div>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <Div sx={{ mt: 1, mb: 1 }}>
              <JumboTextField
                fullWidth
                required
                name="customer.storeCode"
                disabled={true}
                label={t("pages.dsokDetails.storeCode")}
                InputProps={{
                  readOnly: isPartnerAdmin || readOnly,
                }}
              />
            </Div>
          </Grid>
          <Grid item xs={12} md={1} lg={1}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                type="number"
                InputLabelProps={{ required: false }}
                required
                fullWidth
                name="customer.customerId"
                disabled={disabled}
                label={
                  <Label text={t("pages.dsokDetails.customerId")} required />
                }
                InputProps={{
                  value: values.customer?.customerId,
                  readOnly: true,
                  onChange: (e) =>
                    !readOnly &&
                    setFieldValue("customer.customerId", e.target.value),
                }}
              />
            </FormControl>
          </Grid>
        </>
      )}
      <Grid item xs={12} md={4} lg={3}>
        <Div sx={{ mt: 1, mb: 1 }}>
          <JumboTextField
            fullWidth
            required
            name="customer.code"
            disabled={disabled}
            label={t("pages.dsokDetails.code")}
            InputProps={{
              readOnly: true,
            }}
          />
        </Div>
      </Grid>
      <Grid item xs={12} md={12} lg={12}></Grid>
      {(!isPartnerAdmin || readOnly) && (
        <>
          <Grid item xs={12} md={4} lg={2}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                select
                InputLabelProps={{ required: false }}
                name="customer.servicePartnerId"
                label={t("pages.dsokDetails.servicePartner")}
                disabled={servicePartnersLoading || disabled}
                helperText={
                  servicePartnersLoading && !disabled
                    ? t("pages.dsokDetails.servicePartnersLoading", {
                        defaultValue: "Loading…",
                      })
                    : undefined
                }
                InputProps={{
                  endAdornment:
                    servicePartnersLoading && !disabled ? (
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                    ) : undefined,
                }}
              >
                {servicePartnerOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </JumboTextField>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                select
                InputLabelProps={{ required: false }}
                name="customer.posIntegration"
                label={t("pages.dsokDetails.posIntegration")}
                disabled={disabled}
              >
                {POS_INTEGRATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey, { defaultValue: opt.value })}
                  </MenuItem>
                ))}
              </JumboTextField>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                select
                InputLabelProps={{ required: false }}
                name="customer.communicationType"
                label={t("pages.dsokDetails.communicationType")}
                disabled={disabled}
              >
                {COMMUNICATION_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.value}
                  </MenuItem>
                ))}
              </JumboTextField>
            </FormControl>
          </Grid>
          {values?.customer?.communicationType === "DLA" && (
            <Grid item xs={12} md={6} lg={4}>
              <Box sx={{ mt: 1, mb: 1, display: "flex", alignItems: "center", height: 56, gap: 1 }}>
                <Tooltip title={t("dsokCustomerDetail.dla.checkButton", { defaultValue: "Skontrolovať DLA" })}>
                  <span>
                    <IconButton
                      color="primary"
                      disabled={pinging || !values?.customer?.dlaConnectionCode}
                      onClick={handlePingDla}
                    >
                      {pinging ? <CircularProgress size={20} /> : <NetworkCheckIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t("dsokCustomerDetail.dla.downloadLogsButton", { defaultValue: "Stiahnuť logy" })}>
                  <span>
                    <IconButton
                      color="primary"
                      disabled={logsMetaLoading || !values?.customer?.dlaConnectionCode}
                      onClick={handleOpenLogsDialog}
                    >
                      {logsMetaLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
          )}
        </>
      )}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="h6" component="h2">
          {t("pages.dsokDetails.basicSettingsSections.languages")}
        </Typography>
        <Divider sx={{ mt: 1, mb: 2 }} />
      </Grid>
      {(!isPartnerAdmin || readOnly) && (
        <>
          <Grid item xs={12} md={3} lg={3}>
            <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
              <FormControlLabel
                control={
                  <Field
                    name="customer.displayLanguageSwitcher"
                    component={Switch}
                    disabled={disabled}
                    onChange={(e) =>
                      !readOnly &&
                      setFieldValue(
                        "customer.displayLanguageSwitcher",
                        e.target.checked,
                      )
                    }
                  />
                }
                label={t("pages.dsokDetails.displayLanguageSwitcher")}
                checked={values.customer?.displayLanguageSwitcher}
              />
            </Div>
          </Grid>
        </>
      )}
      <Grid item xs={12} md={4} lg={3}>
        <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
          <JumboTextField
            select
            required
            InputLabelProps={{ required: false }}
            name="customer.languages"
            disabled={disabled}
            SelectProps={{
              multiple: true,
            }}
            InputProps={{
              readOnly: isPartnerAdmin || readOnly,
            }}
            label={<Label text={t("pages.dsokDetails.languages")} required />}
          >
            {DSOK_LANGUAGES.map((language, index) => {
              return (
                <MenuItem key={index} value={language.code}>
                  {language.name}
                </MenuItem>
              );
            })}
          </JumboTextField>
        </FormControl>
      </Grid>
      {(!isPartnerAdmin || readOnly) && (
        <>
          <Grid item xs={12} md={4} lg={2}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                select
                required
                InputLabelProps={{ required: false }}
                name="customer.defaultLanguage"
                disabled={disabled}
                label={
                  <Label
                    text={t("pages.dsokDetails.defaultLanguage")}
                    required
                  />
                }
              >
                {DSOK_LANGUAGES.map((language, index) => {
                  return (
                    <MenuItem key={index} value={language.code}>
                      {language.name}
                    </MenuItem>
                  );
                })}
              </JumboTextField>
            </FormControl>
          </Grid>
        </>
      )}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="h6" component="h2">
          {t("pages.dsokDetails.basicSettingsSections.otherSettings")}
        </Typography>
        <Divider sx={{ mt: 1, mb: 2 }} />
      </Grid>
      {(!isPartnerAdmin || readOnly) && (
        <>
          <Grid item xs={12}>
            <Alert severity="info" variant="outlined" sx={{ mt: 1, mb: 1 }}>
              <AlertTitle>
                {t("pages.dsokDetails.frontendTypeHelp.title")}
              </AlertTitle>
              <Typography
                variant="body2"
                component="span"
                sx={{ display: "block", mb: 1 }}
              >
                {t("pages.dsokDetails.frontendTypeHelp.description")}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.5 } }}>
                <li>
                  <Typography variant="body2">
                    {t("pages.dsokDetails.frontendTypeHelp.optionGastrokiosk")}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    {t("pages.dsokDetails.frontendTypeHelp.optionSmartKiosk")}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    {t("pages.dsokDetails.frontendTypeHelp.optionEasyFood")}
                  </Typography>
                </li>
              </Box>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                {t("pages.dsokDetails.frontendTypeHelp.warning")}
              </Typography>
            </Alert>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                type="string"
                InputLabelProps={{ required: false }}
                required
                fullWidth
                name={`customer.beDomain`}
                disabled={disabled}
                label={<Label text={t("pages.dsokDetails.beDomain")} />}
                InputProps={{
                  value: values?.customer?.beDomain,
                  readOnly: true,
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2} lg={1}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                select
                name={`customer.currencyCode`}
                disabled={disabled}
                label={
                  <Label text={t("pages.dsokDetails.currencyCode")} required />
                }
              >
                {CURRENCY_CODES.map(({ code, name }) => {
                  return (
                    <MenuItem key={code} value={code}>
                      {name}
                    </MenuItem>
                  );
                })}
              </JumboTextField>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                required
                select
                InputLabelProps={{ required: false }}
                name={`customer.frontendType`}
                disabled={disabled}
                label={<Label text={t("pages.dsokDetails.frontendType")} />}
              >
                {FRONTEND_TYPE.map((item) => {
                  return (
                    <MenuItem key={item?.code} value={item?.code}>
                      {item?.name}
                    </MenuItem>
                  );
                })}
              </JumboTextField>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                required
                select
                InputLabelProps={{ required: false }}
                name={`customer.lineOfBusiness`}
                disabled={disabled}
                label={<Label text={t("pages.dsokDetails.lineOfBusiness")} />}
              >
                {LINE_OF_BUSINESS.map((item) => {
                  return (
                    <MenuItem key={item?.code} value={item?.code}>
                      {item?.name}
                    </MenuItem>
                  );
                })}
              </JumboTextField>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2} lg={1}>
            <Div sx={{ mt: 1, mb: 1 }}>
              <JumboTextField
                fullWidth
                name="customer.fiskalProPartnerId"
                disabled={disabled}
                label={t("pages.dsokDetails.fiskalProPartnerId")}
              />
            </Div>
          </Grid>
          {frontendType === "easyPay" && frontendType && (
            <>
              <Grid item xs={12} md={2} lg={2}>
                <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                  <JumboTextField
                    required
                    select
                    InputLabelProps={{ required: false }}
                    name={`customer.currencyCodePosition`}
                    disabled={disabled}
                    label={
                      <Label
                        text={t("pages.dsokDetails.currencyCodePosition")}
                      />
                    }
                  >
                    {CURRENCY_CODE_POSITIONS.map((item) => {
                      return (
                        <MenuItem key={item?.code} value={item?.code}>
                          {item?.name}
                        </MenuItem>
                      );
                    })}
                  </JumboTextField>
                </FormControl>
              </Grid>
            </>
          )}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="h6" component="h2">
              {t("pages.dsokDetails.basicSettingsSections.vatSettings")}
            </Typography>
            <Divider sx={{ mt: 1, mb: 2 }} />
          </Grid>
          <Grid item xs={12} md={3} lg={2}>
            <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
              <FormControlLabel
                control={
                  <Field
                    name="customer.vatPayer"
                    component={Switch}
                    disabled={disabled}
                    onChange={(e) =>
                      !readOnly &&
                      setFieldValue("customer.vatPayer", e.target.checked)
                    }
                  />
                }
                label={t("pages.dsokDetails.vatPayer")}
                checked={values.customer?.vatPayer}
              />
            </Div>
          </Grid>
          {isVatPayer && (
            <>
              <Grid item xs={12} md={2} lg={1}>
                <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                  <JumboTextField
                    required
                    select
                    InputLabelProps={{ required: false }}
                    name={`customer.vatCountryCode`}
                    disabled={disabled}
                    label={
                      <Label text={t("pages.dsokDetails.vatCountryCode")} />
                    }
                  >
                    {VAT_COUNTRY_CODES.map((item) => {
                      return (
                        <MenuItem key={item?.code} value={item?.code}>
                          {item?.name}
                        </MenuItem>
                      );
                    })}
                  </JumboTextField>
                </FormControl>
              </Grid>
            </>
          )}
        </>
      )}

      <Dialog open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {t("dsokCustomerDetail.dla.downloadLogsTitle", { defaultValue: "Dostupné logy" })}
        </DialogTitle>
        <DialogContent>
          {logsMetaLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          )}
          {!logsMetaLoading && logDates.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              {t("dsokCustomerDetail.dla.noLogsAvailable", { defaultValue: "Žiadne logy nie sú dostupné" })}
            </Typography>
          )}
          {!logsMetaLoading && logDates.length > 0 && (
            <List dense disablePadding>
              {logDates.map((date) => (
                <ListItem key={date} disablePadding>
                  <ListItemButton
                    disabled={logsDownloading}
                    onClick={() => handleDownloadLogs(date)}
                  >
                    <ListItemText primary={formatDateSk(date)} />
                    {logsDownloading ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" color="action" />}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsDialogOpen(false)}>
            {t("buttons.close", { defaultValue: "Zavrieť" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pingOpen} onClose={() => setPingOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {t("dsokCustomerDetail.dla.modalTitle", { defaultValue: "Stav DLA" })}
        </DialogTitle>
        <DialogContent>
          {pinging && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          )}
          {!pinging && pingResult?.ok === true && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={t("dsokCustomerDetail.dla.online", { defaultValue: "Online" })}
                  color="success"
                  size="small"
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("dsokCustomerDetail.dla.version", { defaultValue: "Verzia" })}
                  </Typography>
                  <Typography variant="body2">{pingResult.data?.version || "-"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("dsokCustomerDetail.dla.uptime", { defaultValue: "Doba behu" })}
                  </Typography>
                  <Typography variant="body2">{formatUptime(pingResult.data?.uptime)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("dsokCustomerDetail.dla.connectedAt", { defaultValue: "Pripojené od" })}
                  </Typography>
                  <Typography variant="body2">{formatDateTime(pingResult.data?.connectedAt)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("dsokCustomerDetail.dla.lastResponse", { defaultValue: "Posledná odpoveď" })}
                  </Typography>
                  <Typography variant="body2">{formatDateTime(pingResult.data?.timestamp)}</Typography>
                </Box>
              </Box>
            </Box>
          )}
          {!pinging && pingResult?.ok === false && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={t("dsokCustomerDetail.dla.offline", { defaultValue: "Offline" })}
                  color="error"
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t("dsokCustomerDetail.dla.offlineMessage", {
                  defaultValue: "DLA nie je pripojená alebo neodpovedá.",
                })}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPingOpen(false)}>
            {t("buttons.close", { defaultValue: "Zavrieť" })}
          </Button>
        </DialogActions>
      </Dialog>

      {!readOnly && (
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>
            {t("confirmDialog.areYouSure") || "Confirm"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {pendingActive
                ? t("pages.dsokDetails.confirmActivateCustomer")
                : t("pages.dsokDetails.confirmDeactivateCustomer")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>
              {t("buttons.cancel") || "Cancel"}
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                setFieldValue("customer.active", pendingActive);
                setConfirmOpen(false);
              }}
            >
              {t("buttons.ok") || "OK"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default BasicSettingsTab;
